import { useState } from "react";

export interface StarRatingProps {
  /** 0-5. The resting display value (the average) — fractional renders a
   *  partial-fill star. */
  value: number;
  /** When true, hovering previews an integer rating over the same row and
   *  clicking calls onChange — there is only ever ONE star row per card,
   *  never a separate display + separate input (that produced 10 star
   *  glyphs on an unrated card — see S-721 live-testing fix). */
  interactive?: boolean;
  /** Fires on click when interactive. Ratings are anonymous-VALUE but now
   *  capped at one vote per person server-side (MarketplaceService.rate) —
   *  pass `disabled` once the caller has already voted this session. */
  onChange?: (rating: number) => void;
  disabled?: boolean;
  ariaLabel?: string;
}

export function StarRating({
  value,
  interactive = false,
  onChange,
  disabled = false,
  ariaLabel,
}: StarRatingProps): JSX.Element {
  const [hovered, setHovered] = useState<number | null>(null);

  const isLive = interactive && !disabled;
  const shown = isLive && hovered !== null ? hovered : value;
  const fillPct = Math.max(0, Math.min(1, shown / 5)) * 100;

  return (
    <span
      className={isLive ? "star-rating star-rating--interactive" : "star-rating"}
      aria-label={ariaLabel ?? `${value.toFixed(1)} out of 5`}
    >
      <span className="star-rating__track" aria-hidden="true">
        ★★★★★
      </span>
      <span className="star-rating__fill" aria-hidden="true" style={{ width: `${fillPct}%` }}>
        ★★★★★
      </span>
      {isLive && (
        <span className="star-rating__hitzones">
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              className="star-rating__hitzone"
              aria-label={`Rate ${n} star${n === 1 ? "" : "s"}`}
              onMouseEnter={() => setHovered(n)}
              onMouseLeave={() => setHovered(null)}
              onFocus={() => setHovered(n)}
              onBlur={() => setHovered(null)}
              onClick={() => onChange?.(n)}
            />
          ))}
        </span>
      )}
    </span>
  );
}
